import { createFileRoute } from "@tanstack/react-router";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { AlarmClockOff, Loader2, Lock, Pencil, Search, Sparkles, UserCheck } from "lucide-react";
import { AdminShell } from "@/components/AdminShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useConfirm } from "@/components/admin/ConfirmDialog";
import { useAuth } from "@/hooks/useAuth";
import { notify } from "@/lib/toast";
import { adImageSrc } from "@/lib/image-src";
import {
  SUPPORT_CATEGORIES,
  SUPPORT_PRIORITIES,
  SUPPORT_SIGNATURE,
  SUPPORT_STATUSES,
  categoryLabel,
  composeSupportReply,
  fillSupportPlaceholders,
  priorityClass,
  priorityLabel,
  shortAge,
  statusLabel,
  stripSupportSignature,
} from "@/lib/support";
import {
  addSupportMessage,
  draftSupportReply,
  getSupportStats,
  getSupportTicket,
  getSupportSignature,
  listSupportMacros,
  listSupportTickets,
  setSupportSignature,
  updateSupportTicket,
} from "@/lib/support-admin.functions";

export const Route = createFileRoute("/_authenticated/admin/support")({
  head: () => ({
    meta: [
      { title: "Support — Sellavo admin" },
      { name: "description", content: "Supportinkorg med köer, kundhistorik och AI-hjälp." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AdminSupportPage,
});

function AdminSupportPage() {
  const [status, setStatus] = useState("open_all");
  const [category, setCategory] = useState("");
  const [priority, setPriority] = useState("");
  const [assigned, setAssigned] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<string | null>(null);

  const fetchList = useServerFn(listSupportTickets);
  const fetchStats = useServerFn(getSupportStats);

  const { data, isPending } = useQuery({
    queryKey: ["support-tickets", status, category, priority, assigned, search],
    queryFn: () =>
      fetchList({ data: { status, category, priority, assigned, search, limit: 100 } }),
    refetchInterval: 30000,
  });
  const { data: stats } = useQuery({ queryKey: ["support-stats"], queryFn: () => fetchStats({}) });

  const counts: Record<string, number> = data?.counts ?? {};
  const openCount =
    (counts["new"] ?? 0) + (counts["open"] ?? 0) + (counts["waiting"] ?? 0);
  const allCount = Object.values(counts).reduce((sum, n) => sum + n, 0);

  return (
    <AdminShell title="Support">
      {stats && (
        <div className="mt-6 grid gap-3 sm:grid-cols-4">
          <StatBox label="Ärenden (30 dgr)" value={stats.total} />
          <StatBox label="Lösta" value={stats.solved} />
          <StatBox label="Väntar på första svar" value={stats.awaitingFirstReply} />
          <StatBox label="Median till svar" value={`${stats.medianFirstReplyMinutes} min`} />
        </div>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-2">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-full sm:w-44" aria-label="Filtrera på status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="open_all">Öppna {countLabel(openCount)}</SelectItem>
            <SelectItem value="all">Alla {countLabel(allCount)}</SelectItem>
            {SUPPORT_STATUSES.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                {s.label} {countLabel(counts[s.id] ?? 0)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={category || "all"} onValueChange={(v) => setCategory(v === "all" ? "" : v)}>
          <SelectTrigger className="w-full sm:w-48" aria-label="Filtrera på ämne">
            <SelectValue placeholder="Alla ämnen" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alla ämnen</SelectItem>
            {SUPPORT_CATEGORIES.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={priority || "all"} onValueChange={(v) => setPriority(v === "all" ? "" : v)}>
          <SelectTrigger className="w-full sm:w-52" aria-label="Filtrera på prioritering">
            <SelectValue placeholder="Alla prioriteringar" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alla prioriteringar</SelectItem>
            {SUPPORT_PRIORITIES.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={assigned || "all"} onValueChange={(v) => setAssigned(v === "all" ? "" : v)}>
          <SelectTrigger className="w-full sm:w-48" aria-label="Filtrera på ansvarig">
            <SelectValue placeholder="Alla ansvariga" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alla ansvariga</SelectItem>
            <SelectItem value="me">Mina</SelectItem>
            <SelectItem value="unassigned">Utan ansvarig</SelectItem>
          </SelectContent>
        </Select>
        <div className="relative min-w-[180px] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Sök rubrik, namn eller e-post"
            className="h-10 pl-8"
          />
        </div>
      </div>

      <p className="mt-2 text-xs text-muted-foreground">
        Siffrorna visar antal ärenden i varje läge. <strong>Snoozad</strong> är ett läge, inte ett
        ämne: ärendet göms från “Öppna” och dyker upp igen automatiskt när tiden gått ut. Kunden får
        ingen notis när du snoozar.
      </p>


      <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,360px)_1fr]">
        <div className="space-y-2">
          {isPending ? (
            <p className="text-sm text-muted-foreground">Laddar…</p>
          ) : !data?.tickets.length ? (
            <p className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              Inga ärenden i den här vyn.
            </p>
          ) : (
            data.tickets.map((t) => (
              <button
                key={t.id}
                onClick={() => setSelected(t.id)}
                className={`w-full rounded-xl border p-3 text-left transition-colors ${
                  selected === t.id
                    ? "border-primary bg-primary/5"
                    : "border-border bg-card hover:bg-accent/50"
                }`}
              >
                <div className="flex items-center gap-2">
                  <span
                    className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${priorityClass(t.priority)}`}
                  >
                    {priorityLabel(t.priority)}
                  </span>
                  <span className="text-xs text-muted-foreground">#{t.ticket_no}</span>
                  <span className="ml-auto text-xs text-muted-foreground">
                    {shortAge(t.last_customer_at)}
                  </span>
                </div>
                <p className="mt-1 truncate text-sm font-semibold">{t.subject}</p>
                <p className="truncate text-xs text-muted-foreground">
                  {t.name || t.email} · {categoryLabel(t.category)} · {statusLabel(t.status)}
                  {t.plan && t.plan !== "free" ? ` · ${t.plan}` : ""}
                </p>
              </button>
            ))
          )}
        </div>

        <div>
          {selected ? (
            <TicketDetail id={selected} />
          ) : (
            <p className="rounded-xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
              Välj ett ärende i listan.
            </p>
          )}
        </div>
      </div>
    </AdminShell>
  );
}

function StatBox({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 font-heading text-2xl font-bold">{value}</p>
    </div>
  );
}

/** Liten siffra efter etiketten i statusfiltret. */
function countLabel(n: number) {
  return <span className="text-muted-foreground tabular-nums">({n})</span>;
}

const SNOOZE_OPTIONS = [
  { value: "4", label: "4 timmar", hours: 4 },
  { value: "24", label: "1 dygn", hours: 24 },
  { value: "72", label: "3 dygn", hours: 72 },
  { value: "0", label: "Ta bort snooze", hours: 0 },
];

function TicketDetail({ id }: { id: string }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { confirm, dialog } = useConfirm();
  const fetchTicket = useServerFn(getSupportTicket);
  const sendMessage = useServerFn(addSupportMessage);
  const patchTicket = useServerFn(updateSupportTicket);
  const fetchMacros = useServerFn(listSupportMacros);
  const aiDraft = useServerFn(draftSupportReply);
  const fetchSignature = useServerFn(getSupportSignature);
  const saveSignature = useServerFn(setSupportSignature);

  const [body, setBody] = useState("");
  const [internal, setInternal] = useState(false);
  const [sigEditing, setSigEditing] = useState(false);
  const [sigDraft, setSigDraft] = useState("");

  const { data, isPending } = useQuery({
    queryKey: ["support-ticket", id],
    queryFn: () => fetchTicket({ data: { id } }),
    refetchInterval: 30000,
  });
  const { data: macros } = useQuery({
    queryKey: ["support-macros"],
    queryFn: () => fetchMacros({}),
  });
  const { data: sigData } = useQuery({
    queryKey: ["support-signature"],
    queryFn: () => fetchSignature({}),
    staleTime: 300000,
  });
  const signature = sigData?.signature ?? SUPPORT_SIGNATURE;

  const sigMutation = useMutation({
    mutationFn: (next: string) => saveSignature({ data: { signature: next } }),
    onSuccess: () => {
      setSigEditing(false);
      notify.success("Hälsningen är uppdaterad", {
        description: "Den används nu i alla svar från hela teamet.",
      });
      void qc.invalidateQueries({ queryKey: ["support-signature"] });
    },
    onError: (e: Error) => notify.error(e.message),
  });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["support-ticket", id] });
    void qc.invalidateQueries({ queryKey: ["support-tickets"] });
  };

  const send = useMutation({
    mutationFn: (solve: boolean) => sendMessage({ data: { id, body: body.trim(), internal, solve } }),
    onSuccess: (_res, solve) => {
      setBody("");
      notify.success(
        internal ? "Anteckning sparad" : solve ? "Svar skickat – ärendet är löst" : "Svar skickat",
      );
      refresh();
    },
    onError: (e: Error) => notify.error(e.message),
  });

  const patch = useMutation({
    mutationFn: (input: Record<string, unknown>) => patchTicket({ data: { id, ...input } as never }),
    onSuccess: refresh,
    onError: (e: Error) => notify.error(e.message),
  });

  const draft = useMutation({
    mutationFn: () => aiDraft({ data: { id } }),
    onSuccess: (res) => {
      setBody(res.draft);
      notify.success("Förslag klart", { description: res.summary });
      refresh();
    },
    onError: (e: Error) => notify.error(e.message),
  });

  if (isPending || !data) {
    return <p className="text-sm text-muted-foreground">Hämtar ärendet…</p>;
  }

  const t = data.ticket;
  const mine = !!user?.id && t.assigned_to === user.id;
  const vars = { name: t.name, ticketNo: t.ticket_no, signature };
  const preview = composeSupportReply(body, vars);

  const changeStatus = async (v: string) => {
    if (v === t.status) return;
    const extra =
      v === "solved"
        ? "Ärendet försvinner ur den öppna kön. Kunden får inget meddelande – skriv ett svar först om hen väntar på ett."
        : v === "spam"
          ? "Ärendet göms ur alla vanliga köer och räknas inte i statistiken. Kunden får inget meddelande."
          : "Bara statusen i vår kö ändras. Kunden får varken mejl eller notis.";
    const ok = await confirm({
      title: `Ändra status till “${statusLabel(v)}”?`,
      description: `Nuvarande status är “${statusLabel(t.status)}”.\n\n${extra}`,
      confirmLabel: `Ändra till ${statusLabel(v)}`,
      destructive: v === "spam",
    });
    if (!ok) return;
    patch.mutate({ status: v });
    notify.success(`Status ändrad till ${statusLabel(v)}`);
  };

  const changePriority = async (v: string) => {
    if (v === t.priority) return;
    const ok = await confirm({
      title: `Ändra prioritering till “${priorityLabel(v)}”?`,
      description: `Nuvarande prioritering är “${priorityLabel(t.priority)}”. Prioriteringen styr bara hur ärendet sorteras och larmar internt – kunden märker ingenting.`,
      confirmLabel: `Ändra till ${priorityLabel(v)}`,
      destructive: false,
    });
    if (!ok) return;
    patch.mutate({ priority: v });
    notify.success(`Prioritering ändrad till ${priorityLabel(v)}`);
  };

  const doSnooze = async (value: string) => {
    const opt = SNOOZE_OPTIONS.find((o) => o.value === value);
    if (!opt) return;
    const back = new Date(Date.now() + opt.hours * 3_600_000).toLocaleString("sv-SE", {
      dateStyle: "full",
      timeStyle: "short",
    });
    const ok = await confirm({
      title: opt.hours ? "Snooza ärendet?" : "Ta bort snoozen?",
      description: opt.hours
        ? `Ärendet göms från “Öppna” och kommer tillbaka ${back}. Kunden får ingen notis och inget svar skickas.`
        : "Ärendet läggs tillbaka i den öppna kön direkt.",
      confirmLabel: opt.hours ? "Snooza" : "Ta bort snooze",
      destructive: false,
    });
    if (!ok) return;
    patch.mutate({ snoozeHours: opt.hours });
    notify.success(opt.hours ? `Snoozat till ${back}` : "Snoozen borttagen");
  };

  const toggleAssign = async () => {
    const ok = await confirm({
      title: mine ? "Lämna ifrån dig ärendet?" : "Ta ärendet?",
      description: mine
        ? "Ärendet blir utan ansvarig och försvinner från din vy “Mina”. Ingen annan blir tilldelad automatiskt."
        : "Du blir ansvarig för ärendet. Det hamnar under “Mina” och andra i teamet ser att du har det.",
      confirmLabel: mine ? "Lämna ifrån mig" : "Ta ärendet",
      destructive: false,
    });
    if (!ok) return;
    patch.mutate(mine ? { unassign: true } : { assignToMe: true });
    notify.success(mine ? "Ärendet är utan ansvarig" : "Du är ansvarig för ärendet");
  };

  const doSend = async (solve: boolean) => {
    if (!body.trim()) return;
    if (internal) {
      send.mutate(false);
      return;
    }
    const ok = await confirm({
      title: solve ? "Skicka svaret och lösa ärendet?" : "Skicka svaret till kunden?",
      description:
        `Detta mejl går till ${t.email}. Exakt så här ser mejlet ut:\n\n` +
        `─────────────\n${preview}\n─────────────` +
        (solve
          ? "\n\nEfter utskicket: ärendet markeras som löst hos oss. Den raden står inte i mejlet – kunden ser bara texten ovan."
          : "\n\nInget annat läggs till i mejlet – kunden ser exakt texten ovan."),
      confirmLabel: solve ? "Skicka och lös" : "Skicka svar",
      destructive: false,
    });
    if (!ok) return;
    send.mutate(solve);
  };

  return (
    <div className="space-y-4">
      {dialog}
      <div className="rounded-xl border border-border bg-card p-4">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="font-heading text-lg font-bold">
            #{t.ticket_no} · {t.subject}
          </h2>
          <span
            className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${priorityClass(t.priority)}`}
          >
            {priorityLabel(t.priority)}
          </span>
        </div>
        <p className="text-sm text-muted-foreground">
          {t.name || "Okänd"} · {t.email} · {categoryLabel(t.category)}
        </p>
        {t.ai_summary && (
          <p className="mt-2 rounded-lg bg-muted/60 p-2 text-sm">
            <strong>Sammanfattning:</strong> {t.ai_summary}
          </p>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Select value={t.status} onValueChange={(v) => void changeStatus(v)}>
            <SelectTrigger className="w-40" aria-label="Ändra status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SUPPORT_STATUSES.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={t.priority} onValueChange={(v) => void changePriority(v)}>
            <SelectTrigger className="w-40" aria-label="Ändra prioritering">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SUPPORT_PRIORITIES.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value="" onValueChange={(v) => void doSnooze(v)}>
            <SelectTrigger className="w-36" aria-label="Snooza ärendet – göm tills senare">
              <SelectValue placeholder="Snooza…" />
            </SelectTrigger>
            <SelectContent>
              {SNOOZE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            size="sm"
            variant={mine ? "secondary" : "outline"}
            title={
              mine
                ? "Du är ansvarig för ärendet. Klicka för att lämna ifrån dig det."
                : "Bli ansvarig för ärendet – det hamnar under “Mina” och andra ser att du tagit det."
            }
            onClick={() => void toggleAssign()}
          >
            <UserCheck className="mr-2 h-4 w-4" />
            {mine ? "Du är ansvarig – lämna ifrån dig" : "Ta ärendet – bli ansvarig"}
          </Button>
        </div>

        {t.snooze_until && (
          <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <span>
              Snoozat till {new Date(t.snooze_until).toLocaleString("sv-SE")} – ärendet ligger gömt
              från “Öppna” tills dess.
            </span>
            <Button size="sm" variant="outline" onClick={() => void doSnooze("0")}>
              <AlarmClockOff className="mr-2 h-4 w-4" /> Avbryt snoozen
            </Button>
          </div>
        )}
      </div>

      {data.customer && (
        <div className="rounded-xl border border-border bg-card p-4 text-sm">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Om kunden
          </p>
          <div className="grid gap-x-6 gap-y-1 sm:grid-cols-2">
            <span>Namn: {data.customer.displayName ?? "–"}</span>
            <span>Kontonivå: {data.customer.plan}</span>
            <span>Annonser: {data.customer.adsCount}</span>
            <span>Inloggning: {data.customer.providers.join(", ") || "–"}</span>
            <span>
              Senast inloggad:{" "}
              {data.customer.lastSignInAt
                ? new Date(data.customer.lastSignInAt).toLocaleString("sv-SE")
                : "–"}
            </span>
            <span>Villkor: {data.customer.consentVersion ?? "–"}</span>
          </div>
          {data.history.length > 0 && (
            <p className="mt-2 text-xs text-muted-foreground">
              Tidigare ärenden: {data.history.map((h) => `#${h.ticket_no}`).join(", ")}
            </p>
          )}
        </div>
      )}

      <div className="space-y-3">
        {data.messages.map((m) => (
          <div
            key={m.id}
            className={`rounded-xl border p-3 text-sm ${
              m.internal
                ? "border-amber-500/40 bg-amber-500/10"
                : m.author_kind === "customer"
                  ? "border-border bg-background"
                  : "border-primary/30 bg-primary/5"
            }`}
          >
            <p className="mb-1 text-xs font-semibold text-muted-foreground">
              {m.internal ? "Intern anteckning · " : ""}
              {m.author_name || (m.author_kind === "customer" ? "Kund" : "Support")} ·{" "}
              {new Date(m.created_at).toLocaleString("sv-SE")}
            </p>
            <p className="whitespace-pre-wrap">{m.body}</p>
            {Array.isArray(m.attachments) && m.attachments.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {(m.attachments as string[]).map((p) => (
                  <a key={p} href={adImageSrc(p)} target="_blank" rel="noreferrer">
                    <img
                      src={adImageSrc(p)}
                      alt="Bilaga"
                      className="h-20 w-20 rounded-md border border-border object-cover"
                    />
                  </a>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-border bg-card p-4">
        {macros && macros.length > 0 && (
          <div className="mb-3 flex flex-wrap gap-2">
            {macros.map((m) => (
              <button
                key={m.id}
                onClick={() =>
                  setBody(stripSupportSignature(fillSupportPlaceholders(m.body, vars)))
                }
                title={fillSupportPlaceholders(m.body, vars)}
                className="rounded-full border border-border px-3 py-1 text-xs font-medium hover:bg-accent"
              >
                {m.title}
              </button>
            ))}
          </div>
        )}
        <Textarea
          rows={6}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={
            internal
              ? "Intern anteckning – syns bara internt, kunden ser den aldrig."
              : `Svar till ${t.name || t.email}. Skriv som du vill att kunden ska läsa det – hälsningen läggs till automatiskt.`
          }
          className={internal ? "border-amber-500/50 bg-amber-500/5" : undefined}
        />
        {!internal && (
          <div className="mt-2 rounded-lg border border-dashed border-border bg-muted/40 p-3 text-sm">
            <div className="mb-1 flex items-center justify-between gap-2">
              <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                <Lock className="h-3 w-3" /> Läggs till automatiskt · låst
              </p>
              {!sigEditing && (
                <button
                  type="button"
                  onClick={() => {
                    setSigDraft(signature);
                    setSigEditing(true);
                  }}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                >
                  <Pencil className="h-3 w-3" /> Lås upp och ändra
                </button>
              )}
            </div>

            {sigEditing ? (
              <div className="space-y-2">
                <Textarea
                  rows={3}
                  value={sigDraft}
                  onChange={(e) => setSigDraft(e.target.value)}
                  aria-label="Gemensam hälsning"
                />
                <p className="text-xs text-muted-foreground">
                  Ändringen gäller alla i teamet och läggs sist i alla kommande svar.
                </p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    disabled={sigDraft.trim().length < 2 || sigMutation.isPending}
                    onClick={async () => {
                      const next = sigDraft.trim();
                      const ok = await confirm({
                        title: "Ändra hälsningen för alla?",
                        description: `Alla svar från hela supportteamet avslutas med:\n\n${next}`,
                        confirmLabel: "Spara hälsningen",
                        destructive: false,
                      });
                      if (ok) sigMutation.mutate(next);
                    }}
                  >
                    Spara för alla
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setSigEditing(false)}>
                    Avbryt
                  </Button>
                </div>
              </div>
            ) : (
              <p className="whitespace-pre-wrap text-foreground/80">{signature}</p>
            )}
          </div>
        )}
        {internal && (
          <p className="mt-2 text-xs font-medium text-amber-600 dark:text-amber-400">
            Syns bara internt – ingen signatur och inget mejl skickas.
          </p>
        )}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={draft.isPending}
            onClick={() => draft.mutate()}
          >
            {draft.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="mr-2 h-4 w-4" />
            )}
            Föreslå svar
          </Button>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4 shrink-0 cursor-pointer accent-[hsl(var(--primary))]"
              checked={internal}
              onChange={(e) => setInternal(e.target.checked)}
            />
            Intern anteckning
          </label>
          <div className="ml-auto flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!body.trim() || send.isPending}
              onClick={() => void doSend(false)}
            >
              {internal ? "Spara anteckning" : "Skicka"}
            </Button>
            {!internal && (
              <Button
                size="sm"
                disabled={!body.trim() || send.isPending}
                onClick={() => void doSend(true)}
              >
                Skicka och lös
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
